import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { forgotPasswordService, verifyPasswordResetCodeService } from "../../services/authService";
import Logo from "../../assets/Images/logo.svg";

const Spinner = () => (
  <svg style={{ width:15, height:15, animation:"spin 1s linear infinite" }} fill="none" viewBox="0 0 24 24">
    <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    <circle style={{ opacity:0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path style={{ opacity:0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const inp = {
  width:"100%", border:"2px solid #f3f4f6", borderRadius:10,
  padding:"10px 14px", fontSize:13, color:"#111827",
  background:"#f9fafb", outline:"none", boxSizing:"border-box", fontFamily:"inherit",
};

/**
 * isModal  – when true renders only the card (no full-page background)
 * onSwitch – called with "login" to change modal view
 */
const ForgotPassword = ({ isModal = false, onSwitch, onClose }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const [code, setCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");
    if (!email.trim()) { setEmailError("Email address is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setEmailError("Enter a valid email address."); return; }
    setLoading(true); setError("");
    try { await forgotPasswordService(email.trim()); setSubmitted(true); }
    catch (err) { setError(err?.response?.data?.message || err?.message || "Something went wrong."); }
    finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifyError("");
    if (!code.trim()) { setVerifyError("Verification code is required."); return; }
    setVerifyLoading(true);
    try {
      const res = await verifyPasswordResetCodeService({ email: email.trim(), code: code.trim() });
      const resetToken = res?.data?.token || res?.token || res?.data || code.trim();
      
      if (isModal && onSwitch) {
        onSwitch("reset-password", { email: email.trim(), token: (typeof resetToken === 'string') ? resetToken : code.trim() });
      } else {
        if (isModal && onClose) {
          onClose();
        }
        navigate(`/reset-password?email=${encodeURIComponent(email.trim())}&token=${encodeURIComponent((typeof resetToken === 'string') ? resetToken : code.trim())}`);
      }
    } catch(err) {
      setVerifyError(err?.response?.data?.message || err?.message || "Invalid or expired code.");
    } finally {
      setVerifyLoading(false);
    }
  };

  const toLogin = isModal && onSwitch
    ? (e) => { e.preventDefault(); onSwitch("login"); }
    : undefined;

  const goBackToLogin = () => {
    if (isModal && onSwitch) { onSwitch("login"); } else { navigate("/"); }
  };

  const card = (
    <div style={{
      position: isModal ? undefined : "relative",
      zIndex: isModal ? undefined : 10,
      width:"100%", maxWidth: isModal ? undefined : 800,
      borderRadius:24, overflow:"hidden", display:"flex",
      boxShadow: isModal ? undefined : "0 40px 120px rgba(0,0,0,0.65)", height:520
    }}>
      {/* Left */}
      <div style={{
        width:280, flexShrink:0, display:"flex", flexDirection:"column", justifyContent:"space-between",
        padding:32, position:"relative", overflow:"hidden",
        background:"linear-gradient(160deg,#1a1710 0%,#201d14 55%,#2e2718 100%)"
      }}>
        <div style={{ position:"absolute", width:200, height:200, borderRadius:"50%", background:"#FEBA06", opacity:0.1, top:-55, right:-55, filter:"blur(40px)", pointerEvents:"none" }} />
        <div style={{ position:"absolute", width:140, height:140, borderRadius:"50%", background:"#FEBA06", opacity:0.06, bottom:-35, left:-35, filter:"blur(32px)", pointerEvents:"none" }} />
        <div style={{ position:"absolute", width:120, height:120, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.06)", top:"30%", right:-22, pointerEvents:"none" }} />

        <div style={{ position:"relative", zIndex:1 }}>
          <img src={Logo} alt="Paving Market" onClick={() => navigate("/")} style={{ height:30, width:"auto", cursor:"pointer", opacity:0.9 }} />
        </div>

        <div style={{ position:"relative", zIndex:1 }}>
          <div style={{ width:28, height:3, background:"#FEBA06", borderRadius:99, marginBottom:18 }} />
          <div style={{ fontSize:30, fontWeight:800, color:"#ffffff", lineHeight:1.15, letterSpacing:"-0.02em" }}>
            Forgot<br />Password?
          </div>
          <p style={{ fontSize:12, color:"#9ca3af", lineHeight:1.6, marginTop:10, maxWidth:200 }}>
            No worries — enter your email and we'll send you a reset link.
          </p>
          <div style={{ marginTop:24 }}>
            <p style={{ fontSize:11, color:"#4b5563", marginBottom:10 }}>Remember it?</p>
            <Link to="/login" onClick={toLogin} style={{ display:"inline-flex", alignItems:"center", gap:8, border:"1px solid rgba(255,255,255,0.2)", color:"#ffffff", fontSize:12, fontWeight:600, padding:"9px 18px", borderRadius:10, textDecoration:"none" }}>
              Back to Sign in
              <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>

        <p style={{ position:"relative", zIndex:1, fontSize:10, color:"#374151" }}>© {new Date().getFullYear()} Paving Market</p>
      </div>

      {/* Right */}
      <div style={{ flex:1, background:"#ffffff", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 28px", borderBottom:"1px solid #f3f4f6" }}>
          {!isModal && (
            <button onClick={() => navigate("/")} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", fontWeight:500, background:"none", border:"none", cursor:"pointer" }}>
              <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Home Page
            </button>
          )}
          <span style={{ fontSize:12, color:"#9ca3af", fontWeight:500, marginLeft:"auto" }}>
            Remember it?{" "}
            <Link to="/login" onClick={toLogin} style={{ fontWeight:700, color:"#FEBA06", textDecoration:"none" }}>Sign in</Link>
          </span>
        </div>

        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px 28px" }}>
          <div style={{ width:"100%", maxWidth:320 }}>
            {submitted ? (
              <div style={{ textAlign:"center" }}>
                <div style={{ width:56, height:56, borderRadius:14, background:"rgba(254,186,6,0.1)", border:"2px solid rgba(254,186,6,0.25)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px" }}>
                  <svg width="26" height="26" fill="none" stroke="#FEBA06" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div style={{ fontSize:22, fontWeight:800, color:"#111827", marginBottom:6 }}>Enter Verification Code</div>
                <p style={{ fontSize:13, color:"#6b7280", marginBottom:4 }}>Code sent to</p>
                <p style={{ fontSize:13, fontWeight:700, color:"#FEBA06", marginBottom:20 }}>{email}</p>

                {verifyError && (
                  <div style={{ display:"flex", alignItems:"flex-start", gap:10, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:13, color:"#dc2626", textAlign:"left" }}>
                    <svg style={{ width:14, height:14, marginTop:1, flexShrink:0 }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {verifyError}
                  </div>
                )}
                <form onSubmit={handleVerify} style={{ display:"flex", flexDirection:"column", gap:14, textAlign:"left", marginBottom: 20 }}>
                  <div>
                    <label style={{ display:"block", fontSize:13, fontWeight:600, color:"#111827", marginBottom:5 }}>
                      MFA Code
                    </label>
                    <input type="text" value={code}
                      onChange={e => { setCode(e.target.value); setVerifyError(""); }}
                      required placeholder="Enter code"
                      style={{ ...inp, textAlign: "center", letterSpacing: "2px", fontWeight: "bold" }}
                      onFocus={e=>{e.target.style.borderColor="#FEBA06";e.target.style.background="#fff";}}
                      onBlur={e=>{e.target.style.borderColor="#f3f4f6";e.target.style.background="#f9fafb";}} />
                  </div>
                  <button type="submit" disabled={verifyLoading} style={{ width:"100%", background:"#FEBA06", color:"#111827", fontWeight:700, fontSize:14, padding:"13px 0", borderRadius:10, border:"none", cursor:"pointer", letterSpacing:"0.03em", boxShadow:"0 6px 20px rgba(254,186,6,0.3)", fontFamily:"inherit", opacity: verifyLoading ? 0.6 : 1 }}>
                    {verifyLoading ? <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}><Spinner />Verifying…</span> : "Verify Code"}
                  </button>
                </form>

                <p style={{ fontSize:12, color:"#9ca3af", marginTop:14 }}>
                  Didn't receive it?{" "}
                  <button onClick={() => { setSubmitted(false); setCode(""); setVerifyError(""); }} style={{ fontWeight:600, color:"#FEBA06", background:"none", border:"none", cursor:"pointer", fontSize:12 }}>Try again</button>
                </p>
                <div style={{ marginTop: 10 }}>
                  <button onClick={goBackToLogin} style={{ width:"100%", background:"transparent", color:"#6b7280", fontWeight:600, fontSize:12, padding:"8px 0", border:"1px solid #d1d5db", borderRadius:10, cursor:"pointer", fontFamily:"inherit" }}>
                    Back to Sign in
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom:24 }}>
                  <div style={{ fontSize:24, fontWeight:800, color:"#111827", letterSpacing:"-0.02em" }}>Reset Password</div>
                  <p style={{ fontSize:13, color:"#6b7280", marginTop:5 }}>We'll email you a reset link</p>
                </div>
                {error && (
                  <div style={{ display:"flex", alignItems:"flex-start", gap:10, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:13, color:"#dc2626" }}>
                    <svg style={{ width:14, height:14, marginTop:1, flexShrink:0 }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </div>
                )}
                <form onSubmit={handleSubmit} style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  <div>
                    <label style={{ display:"block", fontSize:13, fontWeight:600, color:"#111827", marginBottom:5 }}>
                      Email Address
                    </label>
                    <input type="email" value={email}
                      onChange={e => { setEmail(e.target.value); setEmailError(""); setError(""); }}
                      required autoComplete="email" placeholder="you@example.com"
                      style={{ ...inp, borderColor: emailError ? "#ef4444" : "#f3f4f6" }}
                      onFocus={e=>{e.target.style.borderColor=emailError?"#ef4444":"#FEBA06";e.target.style.background="#fff";}}
                      onBlur={e=>{e.target.style.borderColor=emailError?"#ef4444":"#f3f4f6";e.target.style.background="#f9fafb";}} />
                    {emailError && <p style={{ margin:"5px 0 0", fontSize:12, color:"#ef4444" }}>⚠ {emailError}</p>}
                  </div>
                  <button type="submit" disabled={loading} style={{ width:"100%", background:"#FEBA06", color:"#111827", fontWeight:700, fontSize:14, padding:"13px 0", borderRadius:10, border:"none", cursor:"pointer", letterSpacing:"0.03em", boxShadow:"0 6px 20px rgba(254,186,6,0.3)", fontFamily:"inherit", opacity: loading ? 0.6 : 1 }}>
                    {loading ? <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}><Spinner />Sending…</span> : "Send Reset Link"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isModal) return (
    <div style={{ background: "#ffffff", width: "100%" }}>
      {/* Dark header strip */}
      <div style={{ background: "#161412", padding: "16px 32px", display: "flex", alignItems: "center"}}>
        <img src={Logo} alt="Paving Market" style={{ height: 36, width: "auto" }} />
      </div>
      {/* Gold accent bar */}
      <div style={{ height: 3, background: "#FEBA06", width: "100%" }} />
      <div style={{ padding: "28px 32px 28px" }}>
      {submitted ? (
        <div style={{ textAlign:"center" }}>
          <div style={{ width:52, height:52, borderRadius:13, background:"rgba(254,186,6,0.1)", border:"2px solid #FEBA06", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 18px" }}>
            <svg width="24" height="24" fill="none" stroke="#FEBA06" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div style={{ fontSize:20, fontWeight:800, color:"#161412", marginBottom:6 }}>Enter Verification Code</div>
          <p style={{ fontSize:13, color:"#6b7280", marginBottom:4 }}>Code sent to</p>
          <p style={{ fontSize:13, fontWeight:700, color:"#FEBA06", marginBottom:20 }}>{email}</p>

          {verifyError && (
            <div style={{ display:"flex", alignItems:"flex-start", gap:8, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"9px 12px", marginBottom:16, fontSize:12, color:"#dc2626", textAlign:"left" }}>
              {verifyError}
            </div>
          )}

          <form onSubmit={handleVerify} noValidate style={{ display:"flex", flexDirection:"column", gap:16, textAlign:"left", marginBottom: 20 }}>
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#161412", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:7 }}>MFA Code</label>
              <input type="text" value={code} onChange={e => { setCode(e.target.value); setVerifyError(""); }} required placeholder="Enter code"
                style={{ width:"100%", border:`1.5px solid #e5e7eb`, borderRadius:8, padding:"11px 14px", fontSize:13, color:"#161412", background: "#f9fafb", outline:"none", boxSizing:"border-box", fontFamily:"inherit", textAlign: "center", letterSpacing: "2px", fontWeight: "bold" }}
                onFocus={e=>{ e.target.style.borderColor= "#FEBA06"; e.target.style.background="#fff"; }}
                onBlur={e=>{ e.target.style.borderColor= "#e5e7eb"; e.target.style.background= "#f9fafb"; }} />
            </div>
            <button type="submit" disabled={verifyLoading} style={{ width:"100%", background:"#FEBA06", color:"#161412", fontWeight:800, fontSize:13, padding:"13px 0", borderRadius:8, border:"none", cursor:"pointer", letterSpacing:"0.06em", textTransform:"uppercase", fontFamily:"inherit", opacity: verifyLoading ? 0.7 : 1 }}>
              {verifyLoading ? <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}><Spinner />Verifying…</span> : "Verify Code"}
            </button>
          </form>

          <p style={{ fontSize:12, color:"#9ca3af", marginTop:14 }}>
            Didn't receive it?{" "}
            <button onClick={() => { setSubmitted(false); setCode(""); setVerifyError(""); }} style={{ fontWeight:700, color:"#161412", background:"none", border:"none", cursor:"pointer", fontSize:12 }}>Try again</button>
          </p>
          <div style={{ marginTop: 10 }}>
            <button onClick={goBackToLogin} style={{ width:"100%", background:"transparent", color:"#6b7280", fontWeight:700, fontSize:12, padding:"8px 0", border:"1px solid #d1d5db", borderRadius:8, cursor:"pointer", textTransform:"uppercase", fontFamily:"inherit" }}>
              Back to Sign In
            </button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#161412", letterSpacing: "-0.02em" }}>Reset Password</div>
            <p style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>We'll email you a reset link</p>
          </div>
          {error && (
            <div style={{ display:"flex", alignItems:"flex-start", gap:8, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"9px 12px", marginBottom:16, fontSize:12, color:"#dc2626" }}>
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} noValidate style={{ display:"flex", flexDirection:"column", gap:16 }}>
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#161412", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:7 }}>Email Address</label>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setEmailError(""); setError(""); }} autoComplete="email" placeholder="you@example.com"
                style={{ width:"100%", border:`1.5px solid ${emailError ? "#dc2626" : "#e5e7eb"}`, borderRadius:8, padding:"11px 14px", fontSize:13, color:"#161412", background: emailError ? "#fef2f2" : "#f9fafb", outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
                onFocus={e=>{ e.target.style.borderColor= emailError ? "#dc2626" : "#FEBA06"; e.target.style.background="#fff"; }}
                onBlur={e=>{ e.target.style.borderColor= emailError ? "#dc2626" : "#e5e7eb"; e.target.style.background= emailError ? "#fef2f2" : "#f9fafb"; }} />
              {emailError && <p style={{ margin:"5px 0 0", fontSize:11, color:"#dc2626", display:"flex", alignItems:"center", gap:4 }}>⚠ {emailError}</p>}
            </div>
            <button type="submit" disabled={loading} style={{ width:"100%", background:"#FEBA06", color:"#161412", fontWeight:800, fontSize:13, padding:"13px 0", borderRadius:8, border:"none", cursor:"pointer", letterSpacing:"0.06em", textTransform:"uppercase", fontFamily:"inherit", opacity: loading ? 0.7 : 1 }}>
              {loading ? <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}><Spinner />Sending…</span> : "Send Reset Link"}
            </button>
          </form>
          <p style={{ textAlign:"center", marginTop:22, fontSize:13, color:"#6b7280" }}>
            Remember it?{" "}
            <Link to="/login" onClick={toLogin} style={{ color:"#161412", fontWeight:800, textDecoration:"none" }}>Sign in</Link>
          </p>
        </>
      )}
      </div>{/* end padding div */}
    </div>
  );

  return (
    <div style={{
      minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center",
      padding:24, position:"relative", overflow:"hidden",
      background:"linear-gradient(145deg,#161412 0%,#1f1b14 50%,#2a2319 100%)"
    }}>
      <div style={{ position:"absolute", width:480, height:480, borderRadius:"50%", background:"#FEBA06", opacity:0.07, top:-160, right:-140, filter:"blur(85px)", pointerEvents:"none" }} />
      <div style={{ position:"absolute", width:360, height:360, borderRadius:"50%", background:"#FEBA06", opacity:0.05, bottom:-110, left:-110, filter:"blur(70px)", pointerEvents:"none" }} />
      <div style={{ position:"absolute", width:260, height:260, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.04)", top:"8%", left:"4%", pointerEvents:"none" }} />
      <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(circle,rgba(255,255,255,0.022) 1px,transparent 1px)", backgroundSize:"36px 36px", pointerEvents:"none" }} />
      {card}
    </div>
  );
};

export default ForgotPassword;
